// DataHub Role Definitions
// Based on the comprehensive role system design




// ==============================|| PLATFORM-LEVEL ROLES ||============================== //

export const SUPER_ADMIN_ROLE = {
    name: 'super_admin',
    description: 'Full platform access and administration',
    level: 'platform',
    permissions: [
        // Platform permissions
        'organizations:*',
        'users:*',
        'groups:*',
        'system:*',
        'analytics:*',

        // All resource permissions
        'projects:*',
        'storage:*',
        'database:*',
        'jupyter:*',
        'workflow:*',
        'research_docs:*'
    ],
    restrictions: [],
    use_case: 'Platform administrators, DevOps teams'
};

export const ORG_ADMIN_ROLE = {
    name: 'org_admin',
    description: 'Organization-level administration',
    level: 'organization',
    permissions: [
        // Organization management
        'organizations:read',
        'organizations:update',
        'users:*',
        'groups:*',
        'analytics:view_organizations',

        // Project management
        'projects:*',
        'project_team:*',
        'project_settings:*',

        // Resource management
        'storage:*',
        'database:*',
        'jupyter:*',
        'workflow:*',
        'research_docs:*'
    ],
    restrictions: ['Cannot create/delete organizations', 'Cannot access other organizations', 'Cannot modify system settings'],
    use_case: 'Organization leaders, IT managers'
};

// ==============================|| PROJECT-LEVEL ROLES ||============================== //

export const PROJECT_OWNER_ROLE = {
    name: 'project_owner',
    description: 'Full project control and management',
    level: 'project',
    permissions: [
        // Project management
        'projects:*',
        'project_team:*',
        'project_settings:*',

        // Resource access
        'files:*',
        'folders:*',
        'storage_analytics:*',
        'storage_config:admin',

        'collections:*',
        'documents:*',
        'database_admin:*',
        'data_pipeline:*',

        'servers:*',
        'notebooks:*',
        'compute:*',
        'environments:*',

        'dags:*',
        'dag_execution:*',
        'workflow_monitoring:*',
        'workflow_config:*',

        'research_docs:*'
    ],
    restrictions: ['Cannot access other projects', 'Cannot modify organization settings'],
    use_case: 'Project managers, team leads'
};

export const PROJECT_ADMIN_ROLE = {
    name: 'project_admin',
    description: 'Project administration and management',
    level: 'project',
    permissions: [
        // Project management (limited)
        'projects:read',
        'projects:update',
        'project_team:add_member',
        'project_team:remove_member',
        'project_team:assign_role',
        'project_team:view_members',
        'project_settings:configure',

        // Resource management
        'files:*',
        'folders:*',
        'storage_analytics:view_usage',
        'storage_config:manage_quotas',

        'collections:*',
        'documents:*',
        'database_admin:view_stats',
        'data_pipeline:*',

        'servers:*',
        'notebooks:*',
        'compute:view_usage',
        'environments:*',

        'dags:*',
        'dag_execution:*',
        'workflow_monitoring:*',
        'workflow_config:configure_connections',

        'research_docs:*'
    ],
    restrictions: ['Cannot delete project', 'Cannot modify project billing', 'Cannot access other projects'],
    use_case: 'Technical leads, senior developers'
};

export const DATA_SCIENTIST_ROLE = {
    name: 'data_scientist',
    description: 'Advanced data analysis and ML capabilities',
    level: 'project',
    permissions: [
        // Project access
        'projects:read',
        'project_team:view_members',

        // Storage access
        'files:upload',
        'files:download',
        'files:view',
        'files:rename',
        'files:move',
        'files:copy',
        'folders:create',
        'folders:view',
        'storage_analytics:view_usage',

        // Database access
        'collections:view',
        'documents:insert',
        'documents:query',
        'documents:update',
        'documents:export',
        'data_pipeline:create_pipeline',
        'data_pipeline:run_pipeline',
        'data_pipeline:monitor_pipeline',

        // Jupyter access
        'servers:start',
        'servers:stop',
        'servers:view',
        'notebooks:*',
        'compute:request_gpu',
        'compute:request_memory',
        'compute:view_usage',
        'environments:create_env',
        'environments:install_packages',

        // Workflow access
        'dags:view',
        'dags:create',
        'dags:update',
        'dag_execution:trigger',
        'dag_execution:view_logs',
        'workflow_monitoring:view_runs',

        'research_docs:read',
        'research_docs:create',
        'research_docs:ask',
        'research_docs:synthesize'
    ],
    restrictions: ['Cannot delete files/folders', 'Cannot delete collections', "Cannot stop others' servers", 'Cannot delete DAGs'],
    use_case: 'Data scientists, ML engineers'
};

export const DATA_ENGINEER_ROLE = {
    name: 'data_engineer',
    description: 'Data pipeline and infrastructure management',
    level: 'project',
    permissions: [
        // Project access
        'projects:read',
        'project_team:view_members',

        // Storage access
        'files:*',
        'folders:*',
        'storage_analytics:*',

        // Database access
        'collections:*',
        'documents:*',
        'database_admin:manage_indexes',
        'database_admin:view_stats',
        'data_pipeline:*',

        // Jupyter access (limited)
        'servers:start',
        'servers:view',
        'notebooks:view',
        'notebooks:open',
        'compute:view_usage',

        // Workflow access
        'dags:*',
        'dag_execution:*',
        'workflow_monitoring:*',
        'workflow_config:configure_connections',
        'workflow_config:manage_variables',

        'research_docs:*'
    ],
    restrictions: ['Cannot access GPU resources', 'Cannot install packages', 'Cannot modify project settings'],
    use_case: 'Data engineers, ETL developers'
};

export const DATA_ANALYST_ROLE = {
    name: 'data_analyst',
    description: 'Data analysis and reporting capabilities',
    level: 'project',
    permissions: [
        // Project access
        'projects:read',
        'project_team:view_members',

        // Storage access (read-focused)
        'files:download',
        'files:view',
        'folders:view',
        'storage_analytics:view_usage',
        'storage_analytics:view_reports',

        // Database access (read-focused)
        'collections:view',
        'documents:query',
        'documents:export',
        'data_pipeline:monitor_pipeline',

        // Jupyter access
        'servers:start',
        'servers:view',
        'notebooks:create',
        'notebooks:open',
        'notebooks:save',
        'notebooks:export',
        'compute:view_usage',

        // Workflow access (view-only)
        'dags:view',
        'dag_execution:view_logs',
        'workflow_monitoring:view_runs',
        'workflow_monitoring:view_metrics',

        'research_docs:read',
        'research_docs:ask',
        'research_docs:synthesize'
    ],
    restrictions: ['Cannot upload files', 'Cannot modify data', 'Cannot create DAGs', 'Cannot access GPU resources'],
    use_case: 'Business analysts, data analysts'
};

export const VIEWER_ROLE = {
    name: 'viewer',
    description: 'Read-only access to project resources',
    level: 'project',
    permissions: [
        // Project access (read-only)
        'projects:read',
        'project_team:view_members',

        // Storage access (read-only)
        'files:view',
        'files:download',
        'folders:view',
        'storage_analytics:view_usage',

        // Database access (read-only)
        'collections:view',
        'documents:query',
        'documents:export',

        // Jupyter access (view-only)
        'servers:view',
        'notebooks:view',

        // Workflow access (view-only)
        'dags:view',
        'workflow_monitoring:view_runs',

        'research_docs:read',
        'research_docs:ask',
        'research_docs:synthesize'
    ],
    restrictions: ['Cannot modify any resources', 'Cannot create new resources', 'Cannot access compute resources'],
    use_case: 'Stakeholders, external consultants'
};

// ==============================|| ROLE COLLECTION ||============================== //

export const ROLES = {
    SUPER_ADMIN: SUPER_ADMIN_ROLE,
    ORG_ADMIN: ORG_ADMIN_ROLE,
    PROJECT_OWNER: PROJECT_OWNER_ROLE,
    PROJECT_ADMIN: PROJECT_ADMIN_ROLE,
    DATA_SCIENTIST: DATA_SCIENTIST_ROLE,
    DATA_ENGINEER: DATA_ENGINEER_ROLE,
    DATA_ANALYST: DATA_ANALYST_ROLE,
    VIEWER: VIEWER_ROLE
};

// ==============================|| ROLE HELPERS ||============================== //

// Get role by name
export const getRoleByName = (roleName) => {
    const roleKey = Object.keys(ROLES).find((key) => ROLES[key].name === roleName);
    return roleKey ? ROLES[roleKey] : null;
};

// Get all roles by level
export const getRolesByLevel = (level) => {
    return Object.values(ROLES).filter((role) => role.level === level);
};

// Get platform-level roles
export const getPlatformRoles = () => {
    return getRolesByLevel('platform');
};

// Get organization-level roles
export const getOrganizationRoles = () => {
    return getRolesByLevel('organization');
};

// Get project-level roles
export const getProjectRoles = () => {
    return getRolesByLevel('project');
};

// Get role permissions as array
export const getRolePermissions = (roleName) => {
    const role = getRoleByName(roleName);
    return role ? role.permissions : [];
};

// Check if role has specific permission
export const roleHasPermission = (roleName, permission) => {
    const rolePermissions = getRolePermissions(roleName);

    // Check exact permission
    if (rolePermissions.includes(permission)) {
        return true;
    }

    // Check wildcard permissions
    const [resource, action] = permission.split(':');
    const resourceWildcard = `${resource}:*`;
    const superWildcard = '*';

    return rolePermissions.includes(resourceWildcard) || rolePermissions.includes(superWildcard);
};

// Get role description
export const getRoleDescription = (roleName) => {
    const role = getRoleByName(roleName);
    return role ? role.description : '';
};

// Get role restrictions
export const getRoleRestrictions = (roleName) => {
    const role = getRoleByName(roleName);
    return role ? role.restrictions : [];
};

// Get role use case
export const getRoleUseCase = (roleName) => {
    const role = getRoleByName(roleName);
    return role ? role.use_case : '';
};

export default ROLES;
